import {noop} from '@github-ui/noop'
import {CheckIcon} from '@primer/octicons-react'
import {FormControl, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useId, useLayoutEffect, useRef} from 'react'

import {LABELS} from '../../../constants/labels'
import fieldItemStyles from '../../fields/IssueFieldItem.module.css'
import styles from '../ProjectsSections.module.css'
import fieldWrapperStyles from './FieldWrapper.module.css'

type ProjectFieldDisplayContentProps = {
  children: React.ReactNode
  isStatusField?: boolean
  name?: string
}

function ProjectFieldDisplayContent({children, isStatusField, name}: ProjectFieldDisplayContentProps) {
  return (
    <>
      {isStatusField ? (
        <div className={fieldWrapperStyles.fieldContentContainer}>{children}</div>
      ) : (
        <>
          <span className={fieldItemStyles.issueFieldLabel}>{name}</span>
          <>{children}</>
        </>
      )}
    </>
  )
}

type FieldWrapperProps = {
  name: string
  placeholder: string | React.ReactNode
  value?: React.ReactNode
  input?: React.ReactNode
  anchorProps?: React.HTMLAttributes<HTMLElement>
  canUpdate?: boolean
  isStatusField?: boolean
  showInput: boolean
  setShowInput: React.Dispatch<React.SetStateAction<boolean>>
  onCommit?: () => void
  ref?: React.Ref<HTMLLIElement>
  validationMessage?: string
  preventSubmit?: boolean
  isClean?: boolean
  setIsClean?: React.Dispatch<React.SetStateAction<boolean>>
  shouldRestoreRowFocus?: boolean
  setShouldRestoreRowFocus?: React.Dispatch<React.SetStateAction<boolean>>
}

type ProjectFieldDisplayProps = {
  readOnly: boolean
  placeholder?: string
  children: React.ReactNode
  onClick: React.MouseEventHandler<HTMLButtonElement>
  anchorProps?: React.HTMLAttributes<HTMLButtonElement>
  isStatusField?: boolean
  name: string
  isEditing?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export function ProjectFieldDisplay({
  children,
  onClick,
  anchorProps,
  readOnly,
  isStatusField = false,
  isEditing = false,
  placeholder,
  name,
  ref,
}: ProjectFieldDisplayProps) {
  const btnStyles = isStatusField
    ? fieldWrapperStyles.metadataFieldButton
    : clsx(
        styles.projectFieldMetadataContainer,
        fieldItemStyles.issueFieldFormWrapper,
        !readOnly && fieldItemStyles.issueFieldFormWrapperClickable,
        isEditing && fieldItemStyles.isEditing,
      )

  return readOnly ? (
    <div className={btnStyles}>
      <ProjectFieldDisplayContent isStatusField={isStatusField} name={name}>
        {children}
      </ProjectFieldDisplayContent>
    </div>
  ) : (
    <button
      ref={ref}
      type="button"
      aria-label={placeholder}
      onClick={readOnly ? noop : onClick}
      className={btnStyles}
      {...anchorProps}
    >
      <ProjectFieldDisplayContent isStatusField={isStatusField} name={name}>
        {children}
      </ProjectFieldDisplayContent>
    </button>
  )
}

export function FieldWrapper({
  ref,
  name,
  placeholder,
  value,
  input,
  anchorProps,
  showInput,
  setShowInput,
  canUpdate = true,
  isStatusField = false,
  onCommit = noop,
  validationMessage,
  preventSubmit,
  isClean,
  setIsClean,
  shouldRestoreRowFocus = false,
  setShouldRestoreRowFocus,
}: FieldWrapperProps) {
  const localRef = useRef(null)
  const labelId = useId()
  const valueId = useId()
  const buttonRef = useRef<HTMLButtonElement>(null)
  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!anchorProps && canUpdate) {
        setShowInput(true)
      } else if (anchorProps?.onClick) {
        anchorProps.onClick(e)
      }
    },
    [anchorProps, canUpdate, setShowInput],
  )

  // Handle blur to close editor when clicking outside
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      // Check if the new focus target is outside the form control
      if (!e.currentTarget.contains(e.relatedTarget)) {
        if (isClean) {
          // No changes made, just close
          setShowInput(false)
        }
        // If changes were made, keep the editor open
      }
    },
    [isClean, setShowInput],
  )

  useLayoutEffect(() => {
    if (!showInput && shouldRestoreRowFocus) {
      buttonRef.current?.focus()
      setShouldRestoreRowFocus?.(false)
    }
  }, [setShouldRestoreRowFocus, shouldRestoreRowFocus, showInput])

  return (
    <span ref={ref || localRef} className={clsx(isStatusField && styles.ListFieldWrapper)}>
      {showInput && canUpdate ? (
        <div onBlur={handleBlur}>
          <FormControl
            className={clsx(
              fieldItemStyles.issueFieldFormWrapper,
              fieldItemStyles.issueFieldFormWrapperClickable,
              fieldItemStyles.isEditing,
              validationMessage && fieldItemStyles.hasError,
            )}
          >
            <FormControl.Label id={labelId} className={styles.FieldWrapperLabel}>
              <span
                className={
                  isStatusField
                    ? clsx(styles.ListFieldWrapperText, styles.FieldBasisAuto)
                    : clsx(fieldItemStyles.issueFieldLabel)
                }
              >
                {name}
              </span>
            </FormControl.Label>
            {input}
            <IconButton
              aria-label={LABELS.textEditorUpdateButton}
              size="small"
              icon={CheckIcon}
              onClick={
                preventSubmit
                  ? undefined
                  : () => {
                      onCommit()
                      setShowInput(false)
                      setIsClean?.(true)
                      setShouldRestoreRowFocus?.(true)
                    }
              }
              inactive={preventSubmit}
              aria-disabled={preventSubmit}
              className={fieldItemStyles.issueFieldActionButton}
            />
            {validationMessage && (
              <FormControl.Validation variant="error" className={fieldWrapperStyles.fieldItemError}>
                {validationMessage}
              </FormControl.Validation>
            )}
          </FormControl>
        </div>
      ) : (
        <>
          {isStatusField ? (
            <span
              id={labelId}
              className={
                isStatusField
                  ? clsx(styles.ListFieldWrapperText, styles.FieldBasisAuto)
                  : clsx(fieldItemStyles.issueFieldLabel)
              }
            >
              {name}
            </span>
          ) : null}
          <ProjectFieldDisplay
            ref={buttonRef}
            isStatusField={isStatusField}
            anchorProps={isStatusField ? {...anchorProps, 'aria-labelledby': `${labelId} ${valueId}`} : anchorProps}
            onClick={handleClick}
            readOnly={!canUpdate}
            name={name}
            isEditing={showInput}
          >
            <span
              id={isStatusField ? valueId : undefined}
              className={
                value
                  ? isStatusField
                    ? fieldWrapperStyles.fieldValueText
                    : clsx(fieldItemStyles.issueFieldValueText, fieldItemStyles.hasValue)
                  : isStatusField
                    ? fieldWrapperStyles.fieldPlaceholderText
                    : fieldItemStyles.issueFieldValueText
              }
            >
              {value ? value : placeholder}
            </span>
          </ProjectFieldDisplay>
        </>
      )}
    </span>
  )
}
